import { useCallback, useEffect, useState } from 'react';
import { Button, Grid, Modal, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import ZoneDropdown from '../../../components/ZonesDropdownByRegion';
import RegionsDropdown from '../../../components/RegionsDropdown';
import { useAuth } from '../../../hooks/useAuth';
import { CandidateScreeningProgressSummaryFilter } from '../../../shared/VolunteerApplicationServiceTypes';
import { UndoOutlined } from '@ant-design/icons';

const { Text } = Typography;
const { useBreakpoint } = Grid;

interface FilterProps {
	open: boolean;
	onClose: () => void;
	filterValue: CandidateScreeningProgressSummaryFilter;
	onFilterChange: (value: CandidateScreeningProgressSummaryFilter) => void;
}

interface OptionData {
	value: number | undefined;
	label: string | undefined;
}

export const FilterDashboard = ({
	open,
	onClose,
	filterValue,
	onFilterChange,
}: FilterProps) => {
	const { t, i18n } = useTranslation();
	const isLengthyLanguage =
		i18n.language === 'ta' ||
		i18n.language === 'ma' ||
		i18n.language === 'te' ||
		i18n.language === 'ka';
	const screens = useBreakpoint();
	const isMobile = !screens.sm;
	const isStackVertical = isLengthyLanguage && isMobile;
	const { srcmId } = useAuth();
	const [zones, setZones] = useState<Array<string> | undefined>();
	const [regions, setRegions] = useState<OptionData>();

	useEffect(() => {
		setZones(filterValue.zoneNames ?? []);
		setRegions(
			filterValue.regionId !== undefined
				? { value: filterValue.regionId, label: undefined }
				: undefined
		);
	}, [filterValue]);

	const handleZoneChange = useCallback((value: Array<{ value: string }>) => {
		setZones(value.map((z) => z.value));
	}, []);

	const handleRegionsChange = useCallback((value: OptionData) => {
		setZones([]);
		setRegions(value);
	}, []);

	const handleResetFilters = useCallback(() => {
		setZones([]);
		setRegions(undefined);
		onFilterChange({});

		onClose();
	}, [onClose, onFilterChange]);

	const handleFilterButtonClick = useCallback(() => {
		onFilterChange({
			regionId: regions?.value,
			zoneNames: zones,
		});
		onClose();
	}, [onClose, onFilterChange, zones, regions?.value]);

	return (
		<Modal
			open={open}
			onCancel={onClose}
			footer={null}
			centered
			className={
				isLengthyLanguage ? 'md:min-w-[800px]' : 'max-w-md sm:max-w-4xl'
			}
			closable={true}
			maskClosable={true}
		>
			<div className="text-center font-sans font-bold text-lg border-b-[2px] border-gray-700 pb-4">
				{t('filterDashboard')}
			</div>
			<div className="flex flex-col sm:flex-row flex-wrap gap-x-4">
				<div className="w-full sm:w-[calc(50%-0.5rem)]">
					<div className="mb-[16px] flex flex-col gap-2">
						<Text className="block text-xs text-gray-650">
							{t('filterByRegion')}
						</Text>
						<RegionsDropdown
							functionarySrcmId={srcmId}
							value={regions}
							onChange={handleRegionsChange}
						/>
					</div>
				</div>
				<div className="w-full sm:w-[calc(50%-0.5rem)]">
					<div className="mb-[16px] flex flex-col gap-2">
						<Text className="block text-xs text-gray-650">
							{t('filterByZone')}
						</Text>
						<ZoneDropdown
							functionarySrcmId={srcmId}
							value={zones}
							onChange={handleZoneChange}
							regionId={regions?.value}
							key={regions?.value}
						/>
					</div>
				</div>
			</div>
			<div className="flex justify-center">
				<Button
					className="block sm:hidden flex-1 h-9 text-primary-blue font-sans font-bold text-center"
					type="link"
					onClick={handleResetFilters}
				>
					<span>
						{t('resetFilters')}

						<UndoOutlined className="ml-2" />
					</span>
				</Button>
			</div>
			<div
				className={`flex ${
					isStackVertical
						? 'flex-col items-center space-y-2'
						: 'justify-center gap-2'
				} mt-4 ant-modal-footer`}
			>
				<Button
					className="hidden sm:block flex-1 h-9 text-primary-blue font-sans font-bold"
					type="link"
					onClick={handleResetFilters}
				>
					<span>
						{t('resetFilters')}
						<UndoOutlined className="ml-2" />
					</span>
				</Button>
				<Button
					className={`flex-1 h-9 text-primary-blue font-sans font-bold ${
						isStackVertical ? 'p-2' : ''
					}`}
					onClick={onClose}
				>
					{t('closeFilter')}
				</Button>
				<Button
					type="primary"
					onClick={handleFilterButtonClick}
					className={`flex-1 h-9 font-sans font-bold ${
						isStackVertical ? 'p-2 mt-3' : ''
					}`}
				>
					{t('applyFilter')}
				</Button>
			</div>
		</Modal>
	);
};
